import React, { useEffect, useRef, useState } from 'react';
import { Modal, Button, Input, Message, Checkbox, Dimmer, Loader, Menu } from 'semantic-ui-react';
import { useComponentManager } from '../../providers/ComponentManagerProvider';
import { PointDeleteConfirmation } from './PointDeleteConfirmation';

// Maps a current component point to its composite id (the equipment_points FK).
const componentPointId = p => p.id;
const genericIdOf = p => p.generic_point_id ?? p.genericPoint?.id;

const PAGE_SIZE = 25;

const blank = value => (value == null || value === '' ? '' : value);

// Normalize the two point shapes (library result vs. current assignment) into the
// single shape the list/Selected view renders.
const cacheFromResult = gp => ({
    id: gp.id,
    code: gp.code,
    description: gp.description,
    units: gp.units,
    pointType: gp.pointType,
});
const cacheFromCurrent = p => ({
    id: genericIdOf(p),
    code: p.genericPoint?.code,
    description: p.genericPoint?.description,
    units: p.genericPoint?.units,
    pointType: p.genericPoint?.point_type,
});

const POINT_TYPE_TABS = [
    { key: '', label: 'All' },
    { key: 'EP', label: 'Energy' },
    { key: 'VP', label: 'Variable' },
    { key: 'PP', label: 'Position' },
    { key: 'BP', label: 'Binary' },
    { key: 'NP', label: 'Numeric' },
];

// Server-side searchable multi-select over the generic-points library. Current
// assignments are pre-checked and the full selected set is tracked in state
// independent of the current search page, so selections survive searching.
export const ManagePointsModal = ({ open, setOpen, componentId, currentPoints, onSaved, orphanCount = 0 }) => {
    const { getGenericPoints, setComponentPoints, getPointDependents } = useComponentManager();

    // Cascade-confirm state for points being unassigned that have dependents.
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [dependentCount, setDependentCount] = useState(0);

    // The desired set of generic_point_ids (Set of strings). Survives searches.
    const [selectedIds, setSelectedIds] = useState(new Set());

    // Active point-type tab filter ('' = All).
    const [pointType, setPointType] = useState('');

    // When true, show only currently-selected points (resolved from pointById cache).
    const [showSelectedOnly, setShowSelectedOnly] = useState(false);
    // id -> point details, seeded from current assignments and grown from each results page.
    const [pointById, setPointById] = useState(new Map());

    // Controlled search box value vs. the debounced applied query.
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [results, setResults] = useState([]);
    const [total, setTotal] = useState(0);
    const [pageIndex, setPageIndex] = useState(0);

    const [listLoading, setListLoading] = useState(false);
    const [listError, setListError] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState([]);

    // Guards against out-of-order search responses (last query wins).
    const requestToken = useRef(0);
    // Debounce timer for the search box.
    const debounceRef = useRef(null);
    // Tracks the previous `open` value so we seed selections only on the
    // open transition — never when `currentPoints` changes mid-edit (which
    // would clobber the user's in-progress checkbox selections).
    const wasOpen = useRef(false);

    // Seed the selected set from the component's current points on the open
    // transition, and reset search/paging state.
    useEffect(() => {
        if (open && !wasOpen.current) {
            const current = currentPoints ?? [];
            setSelectedIds(new Set(current.map(genericIdOf).filter(Boolean)));
            const seed = new Map();
            current.forEach(p => {
                const e = cacheFromCurrent(p);
                if (e.id) seed.set(e.id, e);
            });
            setPointById(seed);
            setSearchInput('');
            setSearch('');
            setPointType('');
            setShowSelectedOnly(false);
            setPageIndex(0);
            setErrors([]);
        }
        wasOpen.current = open;
    }, [open, currentPoints]);

    // Fetch the current page of library results whenever the query/page changes.
    useEffect(() => {
        // Selected view renders from the cache; no server query needed.
        if (!open || showSelectedOnly) return;
        const token = (requestToken.current += 1);
        setListLoading(true);
        setListError(false);
        getGenericPoints({
            pageSize: PAGE_SIZE,
            pageIndex,
            search: search.trim(),
            sortBy: 'code',
            orderBy: 'asc',
            pointType: pointType || undefined,
        })
            .then(res => {
                if (token !== requestToken.current) return;
                const data = res?.data ?? [];
                setResults(data);
                setTotal(res?.total ?? 0);
                setPointById(prev => {
                    const next = new Map(prev);
                    data.forEach(gp => next.set(gp.id, cacheFromResult(gp)));
                    return next;
                });
                setListLoading(false);
            })
            .catch(() => {
                if (token !== requestToken.current) return;
                setListError(true);
                setListLoading(false);
            });
    }, [open, search, pageIndex, pointType, showSelectedOnly, getGenericPoints]);

    // Clear any pending debounce timer on unmount.
    useEffect(() => () => clearTimeout(debounceRef.current), []);

    const handleSearchChange = value => {
        setSearchInput(value);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setPageIndex(0);
            setSearch(value);
        }, 300);
    };

    const toggle = id => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    // Composite ids of currently-assigned points the user has unchecked.
    const removedComponentPointIds = () =>
        (currentPoints ?? [])
            .filter(p => !selectedIds.has(genericIdOf(p)))
            .map(componentPointId);

    const commitSave = async () => {
        setIsSubmitting(true);
        setErrors([]);
        try {
            await setComponentPoints(componentId, Array.from(selectedIds));
            await onSaved();
            setConfirmOpen(false);
            setOpen(false);
        } catch (error) {
            const message =
                error?.response?.data?.message ||
                error?.message ||
                'Failed to save points. Please try again.';
            setErrors([message]);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSave = async () => {
        setErrors([]);
        const removed = removedComponentPointIds();
        if (removed.length === 0) {
            await commitSave();
            return;
        }
        // Removing points may cascade to equipment_points — warn first.
        setIsSubmitting(true);
        try {
            const res = await getPointDependents(removed);
            const count = res?.count ?? 0;
            setIsSubmitting(false);
            if (count > 0) {
                setDependentCount(count);
                setConfirmOpen(true);
                return;
            }
            await commitSave();
        } catch (error) {
            setIsSubmitting(false);
            const message =
                error?.response?.data?.message ||
                error?.message ||
                'Failed to check dependent equipment. Please try again.';
            setErrors([message]);
        }
    };

    const close = () => {
        // Don't tear down the picker while in-flight, or while the cascade
        // confirm is open (the picker is only hidden behind it, not dismissed).
        if (isSubmitting || confirmOpen) return;
        setOpen(false);
    };

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    // In Selected mode, resolve the selected ids from the cache and filter/sort
    // client-side (the set is small); otherwise show the current server page.
    const q = search.trim().toLowerCase();
    const selectedList = Array.from(selectedIds)
        .map(id => pointById.get(id))
        .filter(Boolean)
        .filter(
            gp =>
                !q ||
                [gp.code, gp.description, gp.id]
                    .filter(Boolean)
                    .some(v => String(v).toLowerCase().includes(q))
        )
        .sort((a, b) => String(a.code || a.id || '').localeCompare(String(b.code || b.id || '')));
    const displayed = showSelectedOnly ? selectedList : results;

    return (
        <>
            {/* Hide the picker while the cascade confirm is open so the two
                Semantic UI modals never stack (which traps clicks behind the dimmer). */}
            <Modal open={open && !confirmOpen} onClose={close} size="small" style={{ textAlign: 'left' }}>
                <Modal.Header>Manage Points</Modal.Header>
                <Modal.Content>
                    {orphanCount > 0 && (
                        <Message info size="small" data-testid="orphan-notice">
                            {orphanCount} orphaned point{orphanCount === 1 ? '' : 's'} on this
                            component {orphanCount === 1 ? 'is' : 'are'} not shown here — manage
                            {orphanCount === 1 ? ' it' : ' them'} from the points list.
                        </Message>
                    )}
                    <Menu pointing secondary size="small" style={{ marginBottom: '0.75rem', flexWrap: 'wrap' }}>
                        {POINT_TYPE_TABS.map((tab, i) => (
                            <React.Fragment key={tab.key}>
                                <Menu.Item
                                    name={tab.label}
                                    active={!showSelectedOnly && pointType === tab.key}
                                    disabled={isSubmitting}
                                    onClick={() => {
                                        setShowSelectedOnly(false);
                                        setPointType(tab.key);
                                        setPageIndex(0);
                                    }}
                                />
                                {/* Selected sits right after All. */}
                                {i === 0 && (
                                    <Menu.Item
                                        active={showSelectedOnly}
                                        disabled={isSubmitting}
                                        onClick={() => setShowSelectedOnly(true)}
                                        data-testid="points-filter-selected"
                                    >
                                        Selected ({selectedIds.size})
                                    </Menu.Item>
                                )}
                            </React.Fragment>
                        ))}
                    </Menu>
                    <Input
                        fluid
                        icon="search"
                        placeholder="Search points by id, code, or description..."
                        value={searchInput}
                        onChange={(e, { value }) => handleSearchChange(value)}
                        disabled={isSubmitting}
                        aria-label="Search points"
                        data-testid="generic-point-search"
                    />
                    <div style={{ marginTop: '0.5rem', color: '#6b7c85', fontSize: '0.85rem' }}>
                        {selectedIds.size} point{selectedIds.size === 1 ? '' : 's'} selected
                    </div>
                    {/* Announce only a concise status to screen readers, not the
                        whole list (which would re-read on every keystroke/page). */}
                    <div
                        aria-live="polite"
                        style={{
                            position: 'absolute',
                            width: '1px',
                            height: '1px',
                            overflow: 'hidden',
                            clip: 'rect(0 0 0 0)',
                        }}
                    >
                        {listLoading
                            ? 'Loading points'
                            : listError
                            ? 'Failed to load points'
                            : showSelectedOnly
                            ? `${displayed.length} point${displayed.length === 1 ? '' : 's'} shown`
                            : `${total} point${total === 1 ? '' : 's'} found`}
                    </div>
                    <div
                        style={{
                            position: 'relative',
                            marginTop: '0.75rem',
                            maxHeight: '45vh',
                            overflowY: 'auto',
                            border: '1px solid #e8e8e8',
                            borderRadius: '4px',
                        }}
                    >
                        <Dimmer active={listLoading} inverted>
                            <Loader size="small">Loading...</Loader>
                        </Dimmer>
                        {listError && !showSelectedOnly ? (
                            <div style={{ padding: '1rem' }}>
                                <Message
                                    negative
                                    size="small"
                                    content="Failed to load points. Adjust your search to retry."
                                />
                            </div>
                        ) : !listLoading && displayed.length === 0 ? (
                            <div style={{ padding: '1rem', color: '#8c979c', fontStyle: 'italic' }}>
                                {showSelectedOnly && !q ? 'No points selected.' : 'No matching points.'}
                            </div>
                        ) : (
                            displayed.map(gp => {
                                const name = blank(gp.code) || blank(gp.id);
                                return (
                                    <label
                                        key={gp.id}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: '0.75rem',
                                            padding: '0.5rem 0.75rem',
                                            borderBottom: '1px solid #f2f2f2',
                                            cursor: isSubmitting ? 'default' : 'pointer',
                                        }}
                                    >
                                        <Checkbox
                                            checked={selectedIds.has(gp.id)}
                                            onChange={() => toggle(gp.id)}
                                            disabled={isSubmitting}
                                            aria-label={name}
                                            data-testid={`generic-point-checkbox-${gp.id}`}
                                        />
                                        <div>
                                            <div style={{ fontWeight: 600, color: '#254a5d' }}>
                                                {name}
                                            </div>
                                            <div style={{ color: '#6b7c85', fontSize: '0.85rem' }}>
                                                {[
                                                    blank(gp.pointType),
                                                    blank(gp.description),
                                                    blank(gp.units),
                                                ]
                                                    .filter(Boolean)
                                                    .join(' · ')}
                                            </div>
                                        </div>
                                    </label>
                                );
                            })
                        )}
                    </div>
                    {!showSelectedOnly && totalPages > 1 && (
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginTop: '0.5rem',
                            }}
                        >
                            <Button
                                size="small"
                                basic
                                icon="chevron left"
                                aria-label="Previous page"
                                disabled={pageIndex === 0 || listLoading || isSubmitting}
                                onClick={() => setPageIndex(i => Math.max(0, i - 1))}
                            />
                            <span style={{ color: '#6b7c85', fontSize: '0.85rem' }}>
                                Page {pageIndex + 1} of {totalPages}
                            </span>
                            <Button
                                size="small"
                                basic
                                icon="chevron right"
                                aria-label="Next page"
                                disabled={pageIndex >= totalPages - 1 || listLoading || isSubmitting}
                                onClick={() => setPageIndex(i => Math.min(totalPages - 1, i + 1))}
                            />
                        </div>
                    )}
                    {errors.length > 0 && (
                        <div style={{ marginTop: '0.75rem' }}>
                            <Message error list={errors} />
                        </div>
                    )}
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={close} disabled={isSubmitting} data-testid="manage-points-cancel">
                        Cancel
                    </Button>
                    <Button
                        className="ui primary button"
                        onClick={handleSave}
                        loading={isSubmitting}
                        disabled={isSubmitting}
                        data-testid="manage-points-save"
                    >
                        Save
                    </Button>
                </Modal.Actions>
            </Modal>
            <PointDeleteConfirmation
                open={confirmOpen}
                setOpen={setConfirmOpen}
                submitAction={commitSave}
                loading={isSubmitting}
                dependentCount={dependentCount}
                title="Save changes to assigned points?"
                confirmLabel="Save changes"
            />
        </>
    );
};
